import { ActionModal, Checkbox, FormError } from '@kcftech/design-system';
import {
    IIndicatorNotificationSubscriptionResponse,
    IIndicatorNotificationTriggerRequestChild,
} from '@kcftech/prometheus-client';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useEditIndicatorAlertsNotificationSubscription } from 'custom_hooks/data_hooks/notification_subscriptions/useEditIndicatorAlertsNotificationSubscription';
import { useSpecificUserInfo } from 'custom_hooks/data_hooks/user/useSpecificUserInfo';
import {
    UnacknowledgedPeriodFormInput,
    unacknowledgedPeriodRules,
} from 'pages/Settings/NotificationSubscriptions/form/IndicatorAlertsNotificationSubscriptionFormContent';
import { EditIndicatorAlertsNotificationSubscriptionFormData } from 'pages/Settings/NotificationSubscriptions/form/types';
import {
    NotificationTriggerTable,
    NotificationTriggerTableWrapper,
} from 'pages/Settings/NotificationSubscriptions/NotificationTriggerTable';
import {
    hasEnabledNotificationMethods,
    isNotificationEventCategory,
    NotificationEventCategory,
    notificationEventCategoryName,
    validateNotificationTriggers,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
    Controller,
    UseFormReturn,
    useForm,
    useWatch,
    useFormState,
} from 'react-hook-form';
import { TimeSpan } from 'utils/TimeSpan';

export type EditIndicatorAlertsNotificationSubscriptionModalProps = Readonly<{
    notificationSubscription: IIndicatorNotificationSubscriptionResponse;
    onClose: (saved: boolean) => void;
}>;

const responseToFormData = (
    response: IIndicatorNotificationSubscriptionResponse,
): EditIndicatorAlertsNotificationSubscriptionFormData => ({
    isSilenced: response.IsSilenced,
    notificationTriggers: Object.fromEntries(
        withMissingIndicatorTriggers(response.NotificationTriggers)
            .filter((nt) => isNotificationEventCategory(nt.ActivityEventCategory))
            .map((nt) => [nt.ActivityEventCategory, nt]),
    ) as {
        [key in NotificationEventCategory]: IIndicatorNotificationTriggerRequestChild;
    },
    unacknowledgedPeriodMs: TimeSpan.parse(
        response.UnacknowledgedPeriod,
    ).asMilliseconds(),
});

/**
 * A Subscription doesn't _necessarily_ contain all possible triggers. We
 * display all possibilities with checkboxes in the UI, though. This adds the
 * necessary triggers as needed (They will later be removed before we serialize
 * the request, if unused)
 */
const withMissingIndicatorTriggers = (
    existing: IIndicatorNotificationTriggerRequestChild[],
): IIndicatorNotificationTriggerRequestChild[] => [
    ...existing,
    ...Object.values(NotificationEventCategory)
        .filter((type) => !existing.some((nt) => nt.ActivityEventCategory === type))
        .map((type) => ({
            ActivityEventCategory: type,
            IsEmailEnabled: false,
            IsSmsEnabled: false,
        })),
];

export function EditIndicatorAlertsNotificationSubscriptionModal({
    notificationSubscription,
    onClose,
}: EditIndicatorAlertsNotificationSubscriptionModalProps) {
    const { showToast } = useNotificationContext();
    const notificationTriggerErrorState = useState<string | undefined>(undefined);

    const user = useSpecificUserInfo(notificationSubscription.User.UserId);
    const userHasPhoneNumber = user.data?.PhoneNumber !== undefined;

    const form = useForm<EditIndicatorAlertsNotificationSubscriptionFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        defaultValues: responseToFormData(notificationSubscription),
    });

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting, errors },
        handleSubmit,
        control,
    } = form;

    const {
        mutate: editIndicatorAlertsNotificationSubscription,
        isPending: isEditingIndicatorAlerts,
    } = useEditIndicatorAlertsNotificationSubscription({
        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content:
                    'Successfully updated indicator alerts notification subscription',
            });
        },
        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update indicator alerts notification subscription',
            }),
    });

    const handleEdit = handleSubmit((formData) => {
        if (
            validateNotificationTriggers(
                Object.values(formData.notificationTriggers),
                userHasPhoneNumber,
            ) !== undefined
        ) {
            return; // this is an error, handled by notificationTriggerErrorState
        }
        editIndicatorAlertsNotificationSubscription({
            id: notificationSubscription.Id,
            isSilenced: formData.isSilenced,
            triggers: Object.values(formData.notificationTriggers).filter(
                (nt) => !!nt.NotificationTriggerId || hasEnabledNotificationMethods(nt),
            ),
            unacknowledgedPeriod: TimeSpan.fromMillis(formData.unacknowledgedPeriodMs),
        });
    });

    return (
        <ActionModal
            title="Edit Indicator Alerts Notification Subscription"
            actionable={
                (!isSubmitted || (isValid && !notificationTriggerErrorState[0])) &&
                !(isValidating || isSubmitting || isEditingIndicatorAlerts)
            }
            onAction={handleEdit}
            onClose={() => onClose(false)}
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={384}
        >
            <IndicatorAlertsTriggersEditor
                form={form}
                errorState={notificationTriggerErrorState}
                userHasPhoneNumber={userHasPhoneNumber}
            />

            <Controller
                control={control}
                name="unacknowledgedPeriodMs"
                rules={unacknowledgedPeriodRules}
                render={({ field: { value, onChange } }) => (
                    <UnacknowledgedPeriodFormInput valueMs={value} onChange={onChange} />
                )}
            />
            <FormError message={errors.unacknowledgedPeriodMs?.message} />
        </ActionModal>
    );
}

function IndicatorAlertsTriggersEditor({
    form,
    errorState,
    userHasPhoneNumber,
}: {
    form: UseFormReturn<EditIndicatorAlertsNotificationSubscriptionFormData>;
    errorState: [string | undefined, Dispatch<SetStateAction<string | undefined>>];
    userHasPhoneNumber: boolean;
}) {
    const {
        control,
    } = form;

    const {
        isSubmitted,
    } = useFormState({
        control,
    });

    const notificationTriggers = useWatch({ control, name: 'notificationTriggers' });

    const [error, setError] = errorState;

    useEffect(() => {
        setError(
            validateNotificationTriggers(
                Object.values(notificationTriggers),
                userHasPhoneNumber,
            ),
        );
    }, [notificationTriggers, setError, userHasPhoneNumber]);

    const rowForEventCategory = (eventCategory: NotificationEventCategory) => (
        <tr key={eventCategory}>
            <td>{notificationEventCategoryName[eventCategory]}</td>
            <td className="text-right">
                <Controller
                    control={control}
                    name={`notificationTriggers.${eventCategory}.IsEmailEnabled` as const}
                    render={({ field: { value, onChange } }) => (
                        <Checkbox
                            label=""
                            onChange={onChange}
                            checked={value}
                            title={`Toggle emails for ${notificationEventCategoryName[eventCategory]}`}
                        />
                    )}
                />
            </td>
            <td className="text-right">
                <Controller
                    control={control}
                    name={`notificationTriggers.${eventCategory}.IsSmsEnabled` as const}
                    render={({ field: { value, onChange } }) => (
                        <Checkbox
                            disabled={!userHasPhoneNumber}
                            label=""
                            onChange={onChange}
                            checked={value}
                            title={`Toggle text messages for ${notificationEventCategoryName[eventCategory]}`}
                        />
                    )}
                />
            </td>
        </tr>
    );

    return (
        <NotificationTriggerTableWrapper>
            <NotificationTriggerTable
                rows={Object.values(NotificationEventCategory).map(rowForEventCategory)}
            />
            <FormError message={isSubmitted ? error : undefined} />
        </NotificationTriggerTableWrapper>
    );
}
